import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { SessionStorageService } from '../services/session-storage.service';
import { LocationImportBatch, LocationImportRow, LocationImportService } from './location-import.service';

@Component({
  selector: 'app-location-import',
  standalone: true,
  imports: [CommonModule, RouterModule, ButtonModule, TableModule, TagModule, ToastModule],
  providers: [MessageService],
  templateUrl: './location-import.component.html',
  styleUrls: ['./location-import.component.css']
})
export class LocationImportComponent implements OnInit {
  file?: File;
  current?: LocationImportBatch;
  history: LocationImportBatch[] = [];
  uploading = false;
  confirming = false;
  downloading = false;
  private companyId?: number;

  constructor(private readonly imports: LocationImportService,
    private readonly session: SessionStorageService,
    private readonly messages: MessageService) {}

  ngOnInit(): void {
    this.companyId = this.session.getCompany()?.id;
    this.loadHistory();
  }

  get canConfirm(): boolean {
    return !!this.current && this.current.status === 'DRAFT' &&
      this.current.totalRows > 0 && this.current.invalidRows === 0 && !this.confirming;
  }

  selectFile(event: Event): void {
    this.file = (event.target as HTMLInputElement).files?.[0];
  }

  downloadTemplate(): void {
    if (!this.companyId) return;
    this.downloading = true;
    this.imports.template(this.companyId).subscribe({
      next: blob => {
        this.save(blob, 'Locations_Template.xlsx');
        this.downloading = false;
      },
      error: error => { this.downloading = false; this.showError(error); }
    });
  }

  upload(): void {
    if (!this.companyId || !this.file) return;
    this.uploading = true;
    this.imports.upload(this.companyId, this.file).subscribe({
      next: response => {
        this.current = response.data;
        this.uploading = false;
        this.loadHistory();
        this.messages.add({ severity: this.current.invalidRows ? 'warn' : 'success',
          summary: 'Workbook reviewed', detail: this.current.invalidRows
            ? 'Fix the listed rows in Excel, then upload the corrected workbook.'
            : 'Every row is valid and ready to confirm.' });
      },
      error: error => { this.uploading = false; this.showError(error); }
    });
  }

  open(batch: LocationImportBatch): void {
    if (!this.companyId) return;
    this.imports.batch(this.companyId, batch.id).subscribe({
      next: response => this.current = response.data,
      error: error => this.showError(error)
    });
  }

  confirm(): void {
    if (!this.companyId || !this.current || !this.canConfirm) return;
    this.confirming = true;
    this.imports.confirm(this.companyId, this.current.id).subscribe({
      next: response => {
        this.current = response.data;
        this.confirming = false;
        this.loadHistory();
        this.messages.add({ severity: this.current.status === 'CONFIRMED' ? 'success' : 'warn',
          summary: this.current.status === 'CONFIRMED' ? 'Locations imported' : 'Workbook needs review',
          detail: this.current.status === 'CONFIRMED'
            ? 'All rows were applied together.'
            : 'References changed after preview. Fix the workbook and upload it again.' });
      },
      error: error => { this.confirming = false; this.showError(error); }
    });
  }

  trackRow(_index: number, row: LocationImportRow): number { return row.rowNumber; }

  private loadHistory(): void {
    if (!this.companyId) return;
    this.imports.history(this.companyId).subscribe({
      next: response => this.history = response.data || [],
      error: error => this.showError(error)
    });
  }

  private save(blob: Blob, fileName: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  private showError(error: any): void {
    this.messages.add({ severity: 'error', summary: 'Locations import',
      detail: typeof error?.error === 'string' ? error.error : error?.error?.message || error?.message || 'Request failed.' });
  }
}
